/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { forwardRef, Fragment, type MouseEvent, useMemo } from 'react';

import { jsx } from '@compiled/react';
import { type Cell, flexRender } from '@tanstack/react-table';
import type { VirtualItem } from '@tanstack/react-virtual';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { CharlieTableProps } from '../../charlie-table.types';
import {
	getFirstNonReservedColumnIndex,
	isFirstNonReservedColumn,
	isReservedColumn,
} from '../../utils/reserved-column.utils';
import { PragmaticRow, type PragmaticRowProps } from '../pragmatic-row';
import { Row } from '../row';
import { RowCell } from '../row-cell';

import { calculateRowPadding } from './body.utils';
import { ExpandRowButton } from './expand-row-button';
import type { SharedRowProps } from './shared-row-props.types';

const styles = cssMap({
	reservedColumnPadding: {
		paddingInlineStart: token('space.100'),
	},
	firstColumnBorder: {
		borderInlineStart: `${token('border.width')} solid ${token('color.border')}`,
	},
	lastColumnBorder: {
		borderInlineEnd: `${token('border.width')} solid ${token('color.border')}`,
	},
	SubRowButtonWrapperStyle: {
		marginInlineEnd: token('space.050'),
	},
	SubRowButtonFirstIndex: {
		marginInlineStart: token('space.negative.100'),
	},
	RowCursorPointer: {
		cursor: 'pointer',
	},
	virtualRow: {
		position: 'absolute',
	},
	columnResponsive: {
		flexGrow: 1,
	},
});

export type DataRowProps<T extends object> = SharedRowProps<T> & {
	visibleCells: Cell<T, unknown>[];
	virtualColumns?: VirtualItem[];
	virtualPaddingLeft?: number;
	virtualPaddingRight?: number;
	tableType?: CharlieTableProps<T>['tableType'];
	enableRowSelection?: CharlieTableProps<T>['enableRowSelection'];
	rowCellXcss?: CharlieTableProps<T>['rowCellXcss'];
	enableKeyboardNavigation?: boolean;
	tableNavigationControllers?: (event: React.KeyboardEvent<HTMLTableCellElement>) => void;
	responsiveColumnSizing?: CharlieTableProps<T>['responsiveColumnSizing'];
	enableRowReorder?: boolean;
};

export function DataRow<T extends object>({
	row,
	table,
	visibleCells,
	virtualColumns,
	virtualPaddingLeft,
	virtualPaddingRight,
	renderOptions,
	rowSpacing,
	tableType,
	onRowClick,
	enableRowSelection,
	rowCellXcss,
	enableKeyboardNavigation,
	tableNavigationControllers,
	responsiveColumnSizing,
	componentPropOverrides,
	labels,
	interactionName,
	analyticsContext,
	enableRowReorder,
	virtualRow,
	rowIndex,
	rowVirtualizer,
}: DataRowProps<T>): JSX.Element {
	const shapeThemeRefresh = fg('charlie-table-shape-refresh');
	const RowComponent = useMemo(() => {
		if (enableRowReorder) {
			return forwardRef<HTMLTableRowElement, PragmaticRowProps<T>>((props, ref) => (
				<PragmaticRow {...props} ref={ref} />
			));
		}
		return Row;
	}, [enableRowReorder]);

	return (
		<RowComponent
			row={row}
			index={virtualRow?.index ?? rowIndex}
			{...(enableRowReorder && { enableRowReorder })}
			data-index={virtualRow?.index}
			key={row.id}
			ref={(node: HTMLTableRowElement | null) =>
				rowVirtualizer ? rowVirtualizer.measureElement(node) : undefined
			}
			spacing={rowSpacing}
			onClick={(e: MouseEvent<HTMLElement>) => {
				// @ts-expect-error Prevent row click if a link was clicked
				if (e.target.closest('a')) {
					return;
				}
				onRowClick?.(row);
			}}
			{...(componentPropOverrides?.row ? { ...componentPropOverrides.row({ row }) } : {})}
			xcss={cx(onRowClick && styles.RowCursorPointer, virtualRow && styles.virtualRow)}
			style={{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				...(virtualRow
					? {
							transform: `translateY(${virtualRow.start}px)`,
						}
					: undefined),
			}}
		>
			{virtualPaddingLeft ? (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				<td style={{ display: 'flex', width: virtualPaddingLeft }} />
			) : null}
			{(virtualColumns ?? visibleCells).map((cellOrVirtualCell, index) => {
				const cell = virtualColumns
					? visibleCells[(cellOrVirtualCell as VirtualItem).index]
					: (cellOrVirtualCell as Cell<T, unknown>);
				const depthAddition = index === 0 ? 1 : 0;
				const cellBackground = renderOptions?.bodyCellBackground?.({
					cell: cell,
					state: table.getState(),
				});
				const allColumns = table.getAllLeafColumns();
				const isFirstNonReserved = isFirstNonReservedColumn(allColumns, index);
				const firstNonReservedColumnIndex = getFirstNonReservedColumnIndex(allColumns);
				const isResponsiveColumn = responsiveColumnSizing?.(cell.column);
				return (
					<RowCell
						isOverridingResponsiveColumnSizing={responsiveColumnSizing != null}
						tableType={tableType}
						columnId={cell.column.id}
						rowSpacing={rowSpacing}
						cellBackground={cellBackground}
						xcss={cx(
							isReservedColumn(cell.column.id) && styles.reservedColumnPadding,
							isResponsiveColumn && styles.columnResponsive,
							cell.row.getCanExpand() && isFirstNonReserved && styles.reservedColumnPadding,
							!shapeThemeRefresh && index === 0 && styles.firstColumnBorder,
							index === 0 && styles.reservedColumnPadding,
							!shapeThemeRefresh && index === visibleCells.length - 1 && styles.lastColumnBorder,
							rowCellXcss,
						)}
						key={cell.id}
						{...(enableKeyboardNavigation && {
							tabIndex: 0,
							onKeyDown: tableNavigationControllers,
						})}
					>
						<Fragment>
							{((row.depth > 0 && isFirstNonReserved) ||
								(firstNonReservedColumnIndex === index &&
									index === 0 &&
									(!virtualPaddingLeft || isFirstNonReserved))) && (
								<Box
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
									style={
										isFirstNonReserved &&
										!row.getParentRow()?.getIsGrouped() &&
										table.getCanSomeRowsExpand()
											? {
													// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
													paddingInlineStart: calculateRowPadding(
														row,
														depthAddition,
														renderOptions,
													),
												}
											: undefined
									}
									xcss={styles.reservedColumnPadding}
								/>
							)}
							{row.getCanExpand() && isFirstNonReserved && (
								<Box
									xcss={cx(
										styles.SubRowButtonWrapperStyle,
										!enableRowSelection && index === 0 && styles.SubRowButtonFirstIndex,
									)}
								>
									<ExpandRowButton
										row={row}
										interactionName={interactionName}
										analyticsContext={analyticsContext}
										labels={labels}
									/>
								</Box>
							)}
							{flexRender(cell.column.columnDef.cell, cell.getContext())}
						</Fragment>
					</RowCell>
				);
			})}
			{virtualPaddingRight ? (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				<td style={{ display: 'flex', width: virtualPaddingRight }} />
			) : null}
		</RowComponent>
	);
}
